import { useEffect, useMemo, useRef, useState } from 'react';

import { useAuth } from '../contexts/AuthContext';
import { useHousehold, useHouseholdDerived } from '../contexts/HouseholdContext';
import { useHouseholdMembership } from '../contexts/HouseholdMembershipContext';
import { useSampleData } from '../contexts/SampleDataContext';
import { getCategoryColor } from '../data/categoryColors';
import { getAnalyticsSpend } from '../services/analyticsService';
import {
  getDashboardRefreshGeneration,
  subscribeDashboardRefresh,
} from '../services/dashboardRefreshBus';
import type { AnalyticsPeriod, AnalyticsCategoryRow, SpendSummaryResponse } from '../types/analytics';
import type {
  DeriveHouseholdOptions,
  HouseholdPeriodKind,
  SpendCategoryNode,
  SpendMerchant,
  SpendTransaction,
} from '../types/financial';

export type SpendBreakdownData = {
  isLive: boolean;
  loading: boolean;
  error: string | null;
  isEmpty: boolean;
  categories: SpendCategoryNode[];
  totalSpend: number;
  period: HouseholdPeriodKind;
  periodKey: string;
  /** Analytics period used for live API calls (month | year). */
  analyticsPeriod: AnalyticsPeriod;
  /** True when we fell back from empty current month to an older month/year. */
  usedPeriodFallback: boolean;
  getCategory: (categoryId: string) => SpendCategoryNode | undefined;
  getMerchant: (
    categoryId: string,
    merchantId: string,
  ) => { category: SpendCategoryNode; merchant: SpendMerchant } | undefined;
  getTransactions: (
    categoryId: string,
    merchantId: string,
  ) => SpendTransaction[];
  refresh: () => void;
};

function resolvePeriodKey(
  asOfMonth: string,
  period: HouseholdPeriodKind,
  periodKey?: string,
): string {
  if (periodKey) return periodKey;
  if (period === 'year' || period === 'ytd') return asOfMonth.slice(0, 4);
  return asOfMonth;
}

function toAnalyticsPeriod(period: HouseholdPeriodKind): AnalyticsPeriod {
  if (period === 'year' || period === 'ytd') return 'year';
  return 'month';
}

/** Current UTC period key for live analytics (ignore sample asOfMonth). */
function currentAnalyticsPeriodKey(period: AnalyticsPeriod): string {
  const now = new Date();
  if (period === 'year') return String(now.getUTCFullYear());
  return `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function shiftMonthKey(periodKey: string, deltaMonths: number): string {
  const [year, month] = periodKey.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1 + deltaMonths, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
}

export function formatMonthKeyLabel(periodKey: string): string {
  const [year, month] = periodKey.split('-').map(Number);
  if (!year || !month) return periodKey;
  const date = new Date(Date.UTC(year, month - 1, 1));
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric', timeZone: 'UTC' });
}

function isEmptySpend(response: SpendSummaryResponse): boolean {
  return response.totalSpend === 0 && response.categories.length === 0;
}

function mapLiveCategories(rows: AnalyticsCategoryRow[]): SpendCategoryNode[] {
  return rows.map((row, index) => ({
    id: row.categorySlug,
    name: row.categoryName,
    amount: Math.abs(row.totalAmount),
    color: getCategoryColor(row.categorySlug, index),
    transactionCount: row.transactionCount,
    merchants: [],
  }));
}

/**
 * Statements often land in a prior billing month. If the requested month is empty,
 * walk back a few months, then try the calendar year.
 */
async function loadSpendWithFallback(options: {
  accessToken: string;
  period: AnalyticsPeriod;
  periodKey: string;
  view?: 'personal' | 'combined';
  forceRefresh?: boolean;
  /** When false, return empty month as-is (user picked a specific month). */
  allowEmptyFallback?: boolean;
}): Promise<{ response: SpendSummaryResponse; usedFallback: boolean; analyticsPeriod: AnalyticsPeriod }> {
  const primary = await getAnalyticsSpend(options);
  if (
    options.period !== 'month' ||
    !isEmptySpend(primary) ||
    options.allowEmptyFallback === false
  ) {
    return { response: primary, usedFallback: false, analyticsPeriod: options.period };
  }

  for (const delta of [-1, -2, -3]) {
    const periodKey = shiftMonthKey(options.periodKey, delta);
    const retry = await getAnalyticsSpend({
      accessToken: options.accessToken,
      period: 'month',
      periodKey,
      view: options.view,
      forceRefresh: options.forceRefresh,
    });
    if (!isEmptySpend(retry)) {
      return { response: retry, usedFallback: true, analyticsPeriod: 'month' };
    }
  }

  const yearKey = options.periodKey.slice(0, 4);
  const year = await getAnalyticsSpend({
    accessToken: options.accessToken,
    period: 'year',
    periodKey: yearKey,
    view: options.view,
    forceRefresh: options.forceRefresh,
  });
  if (!isEmptySpend(year)) {
    return { response: year, usedFallback: true, analyticsPeriod: 'year' };
  }

  return { response: primary, usedFallback: false, analyticsPeriod: options.period };
}

export function useSpendBreakdown(
  options?: DeriveHouseholdOptions,
): SpendBreakdownData {
  const { isSampleMode } = useSampleData();
  const { isAuthenticated, tokens } = useAuth();
  const { view: householdView, viewReady } = useHouseholdMembership();
  const { core, refresh: refreshHousehold } = useHousehold();
  const useLive = isAuthenticated && !isSampleMode && !!tokens?.accessToken;
  const period = options?.period ?? 'month';
  const requestedAnalyticsPeriod = toAnalyticsPeriod(period);
  const currentKey = currentAnalyticsPeriodKey(requestedAnalyticsPeriod);
  // Explicit periodKey (month picker) wins; otherwise use calendar current.
  const liveRequestPeriodKey =
    options?.periodKey && /^\d{4}(-\d{2})?$/.test(options.periodKey)
      ? options.periodKey
      : currentKey;

  /**
   * Sample/fixture spend is anchored to profile.asOfMonth (e.g. 2026-07), not
   * calendar "now". Treat the current calendar month as "use fixture default"
   * so guests don't land on an empty Aug while Year still has data.
   * Past months from the navigator are honored as-is.
   */
  let derivePeriodKey = options?.periodKey;
  if (!useLive && period === 'month') {
    if (!derivePeriodKey || derivePeriodKey === currentKey) {
      derivePeriodKey = undefined; // → resolvePeriodKey uses asOfMonth
    }
  }
  const periodKey = resolvePeriodKey(core.profile.asOfMonth, period, derivePeriodKey);
  const derived = useHouseholdDerived({ period, periodKey });
  const allowEmptyFallback =
    requestedAnalyticsPeriod !== 'month' || liveRequestPeriodKey === currentKey;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [liveCategories, setLiveCategories] = useState<SpendCategoryNode[] | null>(null);
  const [liveTotalSpend, setLiveTotalSpend] = useState(0);
  const [livePeriodKey, setLivePeriodKey] = useState(periodKey);
  const [liveAnalyticsPeriod, setLiveAnalyticsPeriod] =
    useState<AnalyticsPeriod>(requestedAnalyticsPeriod);
  const [usedPeriodFallback, setUsedPeriodFallback] = useState(false);
  const [tick, setTick] = useState(0);
  /** 0 = never synced; catches imports that completed while Spend was unmounted. */
  const seenGenerationRef = useRef(0);
  const hasLiveRef = useRef(false);
  hasLiveRef.current = liveCategories != null;

  useEffect(() => {
    const applyGeneration = (generation: number) => {
      if (generation === seenGenerationRef.current) return;
      seenGenerationRef.current = generation;
      setTick((v) => v + 1);
      void refreshHousehold();
    };

    // Catch refreshes that fired while this tab was unmounted.
    applyGeneration(getDashboardRefreshGeneration());

    return subscribeDashboardRefresh(applyGeneration);
  }, [refreshHousehold]);

  useEffect(() => {
    if (!useLive || !tokens?.accessToken || !viewReady) {
      if (!useLive || !tokens?.accessToken) {
        setLiveCategories(null);
        setLiveTotalSpend(0);
        setUsedPeriodFallback(false);
        setLiveAnalyticsPeriod(requestedAnalyticsPeriod);
        setError(null);
      }
      return;
    }

    let cancelled = false;
    setError(null);
    if (!hasLiveRef.current) {
      setLoading(true);
    }

    const forceRefresh = true;

    void loadSpendWithFallback({
      accessToken: tokens.accessToken,
      period: requestedAnalyticsPeriod,
      periodKey: liveRequestPeriodKey,
      view: householdView,
      forceRefresh,
      allowEmptyFallback,
    })
      .then(({ response, usedFallback, analyticsPeriod }) => {
        if (cancelled) return;
        const mapped = mapLiveCategories(response.categories);
        setLiveCategories(mapped);
        setLiveTotalSpend(Math.abs(response.totalSpend));
        setLivePeriodKey(response.periodKey);
        setLiveAnalyticsPeriod(analyticsPeriod);
        setUsedPeriodFallback(usedFallback);
        setLoading(false);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setError(err.message);
        if (!hasLiveRef.current) {
          setLiveCategories(null);
          setLiveTotalSpend(0);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [
    useLive,
    tokens?.accessToken,
    requestedAnalyticsPeriod,
    liveRequestPeriodKey,
    allowEmptyFallback,
    tick,
    householdView,
    viewReady,
  ]);

  return useMemo(() => {
    const refresh = () => {
      setTick((v) => v + 1);
      void refreshHousehold();
    };

    if (!useLive) {
      const categories = derived.spendTree;
      const totalSpend =
        categories.reduce((sum, c) => sum + c.amount, 0) || derived.summaries.totalSpend;

      const getCategory = (categoryId: string) =>
        categories.find((c) => c.id === categoryId);

      const getMerchant = (categoryId: string, merchantId: string) => {
        const category = getCategory(categoryId);
        if (!category) return undefined;
        const merchant = category.merchants.find((m) => m.id === merchantId);
        if (!merchant) return undefined;
        return { category, merchant };
      };

      const getTransactions = (categoryId: string, merchantId: string) =>
        getMerchant(categoryId, merchantId)?.merchant.transactions ?? [];

      return {
        isLive: false,
        loading: false,
        error: null,
        isEmpty: categories.length === 0 && totalSpend === 0,
        categories,
        totalSpend,
        period,
        periodKey,
        analyticsPeriod: requestedAnalyticsPeriod,
        usedPeriodFallback: false,
        getCategory,
        getMerchant,
        getTransactions,
        refresh,
      };
    }

    const categories = liveCategories ?? [];
    const totalSpend = liveTotalSpend;
    const resolvedKey = livePeriodKey || liveRequestPeriodKey;

    const getCategory = (categoryId: string) =>
      categories.find((c) => c.id === categoryId);

    const getMerchant = (categoryId: string, merchantId: string) => {
      const category = getCategory(categoryId);
      if (!category) return undefined;
      const merchant = category.merchants.find((m) => m.id === merchantId);
      if (!merchant) return undefined;
      return { category, merchant };
    };

    const getTransactions = (categoryId: string, merchantId: string) =>
      getMerchant(categoryId, merchantId)?.merchant.transactions ?? [];

    return {
      isLive: true,
      loading,
      error,
      isEmpty: totalSpend === 0 && categories.length === 0,
      categories,
      totalSpend,
      period,
      periodKey: resolvedKey,
      analyticsPeriod: liveAnalyticsPeriod,
      usedPeriodFallback,
      getCategory,
      getMerchant,
      getTransactions,
      refresh,
    };
  }, [
    useLive,
    derived,
    period,
    periodKey,
    requestedAnalyticsPeriod,
    liveRequestPeriodKey,
    liveCategories,
    liveTotalSpend,
    livePeriodKey,
    liveAnalyticsPeriod,
    usedPeriodFallback,
    loading,
    error,
    refreshHousehold,
  ]);
}
